import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { User, Mail, Save, Store } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, token, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await fetch(`${API}/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name, bio, avatar: avatar || null })
      });

      if (res.ok) {
        const updated = await res.json();
        updateUser(updated);
        toast.success('Profile updated!');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (e) {
      toast.error('Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3 mb-8">
          <User className="w-8 h-8" />
          My Profile
        </h1>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Avatar Preview */}
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-2xl bg-pastel-art/30 overflow-hidden border-2 border-primary/10 flex items-center justify-center">
              {avatar ? (
                <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <Label htmlFor="avatar">Avatar URL</Label>
              <Input
                id="avatar"
                type="url"
                placeholder="https://example.com/avatar.jpg"
                value={avatar}
                onChange={(e) => setAvatar(e.target.value)}
                className="input-brutal mt-1"
              />
            </div>
          </div>

          <div className="card-brutal p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-brutal"
                required
                data-testid="profile-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={user?.email}
                  className="pl-10 input-brutal bg-muted"
                  disabled
                />
              </div>
              <p className="text-sm text-muted-foreground">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="min-h-[100px]"
                data-testid="profile-bio"
              />
            </div>
          </div>

          {user?.is_creator && (
            <div className="card-brutal p-6 bg-pastel-stickers/10">
              <div className="flex items-center gap-3 mb-4">
                <Store className="w-6 h-6" />
                <h2 className="font-heading font-bold text-lg">Creator Account</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                You have a creator account. Manage your store and products from your dashboard.
              </p>
              <Link to="/dashboard">
                <Button variant="outline" className="btn-brutal">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full btn-brutal-primary"
            disabled={saving}
            data-testid="save-profile-btn"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
}
